import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { articlesData } from './ArticlesPage';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const article = articlesData.find(a => a.slug === slug);

  if (!article) {
    return <Navigate to="/articles" replace />;
  }

  // Fonction pour rendre le contenu markdown en HTML basique
  const renderContent = (content) => {
    return content
      .split('\n')
      .map((line, index) => {
        // Titres H2
        if (line.startsWith('## ')) {
          return (
            <h2 key={index} className="text-2xl md:text-3xl font-bold font-heading text-[#0a1628] mt-12 mb-6">
              {line.replace('## ', '')}
            </h2>
          );
        }
        // Titres H3
        if (line.startsWith('### ')) {
          return (
            <h3 key={index} className="text-xl md:text-2xl font-semibold font-heading text-[#0a1628] mt-8 mb-4">
              {line.replace('### ', '')}
            </h3>
          );
        }
        // Séparateurs
        if (line.trim() === '---') {
          return <hr key={index} className="my-8 border-slate-200" />;
        }
        // Listes
        if (line.startsWith('- ')) {
          return (
            <li key={index} className="flex items-start text-slate-600 mb-2">
              <span className="w-2 h-2 rounded-full bg-[#BFA15F] mr-3 mt-2 flex-shrink-0" />
              <span>{renderInlineStyles(line.replace('- ', ''))}</span>
            </li>
          );
        }
        // Lignes vides
        if (line.trim() === '') {
          return <div key={index} className="h-4" />;
        }
        // Paragraphes normaux
        return (
          <p key={index} className="text-slate-600 leading-relaxed mb-4">
            {renderInlineStyles(line)}
          </p>
        );
      });
  };

  // Fonction pour rendre les styles inline (bold, italic)
  const renderInlineStyles = (text) => {
    // Remplacer **text** par du gras
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-[#0a1628]">{part.slice(2, -2)}</strong>;
      }
      // Remplacer *text* par de l'italique
      if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  return (
    <div data-testid="article-detail-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-slate-50">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            {/* Back link */}
            <Link 
              to="/articles" 
              className="inline-flex items-center text-sm text-slate-500 hover:text-[#0a1628] transition-colors mb-8"
            >
              <ArrowLeft size={16} className="mr-2" />
              Retour aux articles
            </Link>
            
            {/* Meta */}
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
              <span className="flex items-center gap-2">
                <Calendar size={14} />
                {new Date(article.date).toLocaleDateString('fr-FR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
              <span>·</span>
              <span className="flex items-center gap-2">
                <Clock size={14} />
                {article.readTime} de lecture
              </span>
            </div>
            
            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-[#0a1628] leading-tight mb-6">
              {article.title}
            </h1>
            
            {/* Accroche */}
            <p className="text-xl text-[#BFA15F] font-medium mb-6">
              {article.accroche}
            </p>
            
            {/* Tags */}
            {article.tags && (
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, idx) => (
                  <span key={idx} className="text-xs px-3 py-1 bg-white border border-slate-200 text-slate-600">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Image */}
      <section className="bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto -mt-8">
            <div className="aspect-video overflow-hidden shadow-xl">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <article className="max-w-3xl mx-auto prose-custom">
            {renderContent(article.content)}
          </article>
        </div>
      </section>

      {/* Author & CTA */}
      <section className="py-16 bg-slate-50">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-white border border-slate-100">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[#BFA15F] font-semibold mb-2">
                  À propos de l'auteur
                </p>
                <h3 className="font-heading font-bold text-xl text-[#0a1628] mb-2">
                  Yoann Cohen
                </h3>
                <p className="text-sm text-slate-600">
                  Directeur Transformation indépendant, spécialisé dans l'accompagnement 
                  des organisations dans leurs projets de transformation.
                </p>
              </div>
              <a 
                href="mailto:yoann.cohen@wyco.fr"
                className="btn-primary whitespace-nowrap"
              >
                Me contacter
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* More Articles */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container-main">
          <div className="text-center">
            <Link 
              to="/articles"
              className="btn-secondary"
            >
              <ArrowLeft size={16} className="mr-2" />
              Voir tous les articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleDetailPage;
